import React from "react";
import axios from "axios";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MK = { moneyline: "ML", puckline: "PL", total: "TOT" };
const RANK = { APPROVED: 0, VALUE: 1, WATCH: 2 };
const TONE = { APPROVED: "bg-lime-500/15 text-lime-300", VALUE: "bg-amber-500/15 text-amber-300", WATCH: "bg-cyan-500/15 text-cyan-300" };
const odds = (o) => (o == null ? "—" : o > 0 ? `+${o}` : `${o}`);
const val = (v, s = "") => (v == null ? "—" : `${v}${s}`);

const Block = ({ title, children, testId }) => (
  <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-2.5" data-testid={testId}>
    <p className="mb-1.5 font-mono text-[9px] uppercase tracking-widest text-cyan-400">{title}</p>
    <div className="space-y-0.5 font-mono text-[10px] text-slate-300">{children}</div>
  </div>
);

const DIR = ["history", "agreement", "h2h"];
const QUAL = ["completeness", "alignment", "sample"];
const SupportBreakdown = ({ w }) => {
  const p = w.support_parts || {};
  const wt = w.support_weights || {};
  const row = (keys) => keys.filter((k) => p[k] != null).map((k) => `${k} ${p[k]} (${wt[k]}%)`).join(" · ");
  return (
    <div data-testid="gl-sup-score">
      <p>Support {w.support}/100 = 50 + (direction {p.direction} − 50) × quality {p.quality}%</p>
      <p className="text-slate-500">Direction: {row(DIR) || "no evidence"}</p>
      <p className="text-slate-500">Quality: {row(QUAL)}</p>
    </div>
  );
};

const Why = ({ w }) => (
  <Block title="Why the model likes it" testId="gl-sup-why">
    <p className="text-lime-300">{w.status_reason}</p>
    {w.support != null && <SupportBreakdown w={w} />}
    {w.form_adj_pts != null && <p data-testid="gl-sup-form-adj">Puck-line form adjustment {w.form_adj_pts > 0 ? "+" : ""}{w.form_adj_pts} pts ({w.pre_form_model_pct}% → {w.model_pct}%)</p>}
    <p className="text-slate-500" data-testid="gl-sup-integrity">Data checks: {w.integrity === "OK" ? "all passed" : JSON.stringify(w.integrity)}</p>
    {(w.support_notes || []).map((n) => <p key={n} className="text-slate-500">• {n}</p>)}
    <p>Price {odds(w.odds)} · model {val(w.model_pct, "%")} vs market no-vig {val(w.novig_pct, "%")} · fair {odds(w.fair)}</p>
    <p>Edge {val(w.edge, " pts")} · expected ROI {val(w.exp_roi, "%")} · risk {w.risk}</p>
    <p className="text-slate-500">Raw model {val(w.raw_model_pct, "%")} before the calibration pull · projected {w.exp_away}–{w.exp_home} ({w.model_total} goals, margin {w.model_margin > 0 ? "+" : ""}{w.model_margin} home, incl. OT/SO and empty-net goals)</p>
    <p className="text-slate-500">Goalies {w.goalie_away || "TBD"} / {w.goalie_home || "TBD"} {w.goalie_confirmed ? "· confirmed" : "· provisional"}</p>
    {w.goalie_inputs && (
      <p className="text-slate-500" data-testid="gl-sup-goalie-inputs">
        Goalie input: {["away", "home"].map((sd) => {
          const g = w.goalie_inputs[sd] || {};
          const f = (w.xg_inputs || {})[`goalie_${sd}`];
          return `${g.goalie || "TBD"} ${g.save_pct != null ? `sv ${(g.save_pct * 100).toFixed(1)}% on ${g.shots_faced} shots vs team ${(g.team_save_pct * 100).toFixed(1)}%` : "· no NHL.com history matched (no adjustment)"}${f ? ` → opponent goals ×${f}` : ""}`;
        }).join(" · ")}
      </p>
    )}
  </Block>
);

const H2H = ({ h, away, home }) => (
  <Block title="Head-to-head · this & last season" testId="gl-sup-h2h">
    {h.n ? (<>
      <p>{away} {h.away_wins} – {h.home_wins} {home} · avg total {h.avg_total} · {h.one_goal} one-goal games</p>
      {h.games.slice(0, 6).map((g) => (
        <p key={g.date} className="text-slate-500">{g.date} · {g.away} {g.as} @ {g.home} {g.hs}{g.end !== "REG" ? ` (${g.end})` : ""}</p>
      ))}
    </>) : <p className="text-slate-500">No meetings this or last season yet.</p>}
  </Block>
);

const Trend = ({ t }) => (
  <p data-testid={`gl-sup-trend-${t.team}`}>
    <span className="font-bold text-slate-100">{t.team}</span>{" "}
    {t.gp ? `${t.record} · L10 ${t.l10} · home ${t.home} · road ${t.road} · GF ${t.gf_pg} / GA ${t.ga_pg} · L10 ${t.l10_gf_pg}/${t.l10_ga_pg} · ${t.streak} · rest ${t.rest_days}d` : "no games yet"}
    <span className="text-slate-600"> ({t.sample})</span>
  </p>
);

const Detail = ({ d }) => (
  <div className="grid grid-cols-1 gap-2 lg:grid-cols-2" data-testid={`gl-sup-detail-${d.event_id}-${d.market}`}>
    <Why w={d.why} />
    <H2H h={d.h2h} away={d.trends.away.team} home={d.trends.home.team} />
    <Block title="Team trends" testId="gl-sup-trends"><Trend t={d.trends.away} /><Trend t={d.trends.home} /></Block>
    <Block title="Market trend · last 10" testId="gl-sup-market">
      {Object.values(d.market_trend).map((m, i) => <p key={i}>{m.n ? m.label : "No games yet."}</p>)}
    </Block>
  </div>
);

const Item = ({ g, mk, m, apiDate }) => {
  const [d, setD] = React.useState(null);
  const load = () => {
    if (d) return;
    axios.get(`${API}/game-lines/support`, { params: { event_id: g.event_id, market: mk, date: apiDate } })
      .then((r) => setD(r.data)).catch(() => setD({ error: true }));
  };
  const id = `${g.event_id}-${mk}`;
  return (
    <AccordionItem value={id} className="border-slate-800">
      <AccordionTrigger onClick={load} className="px-2 py-2 font-mono text-[11px] hover:no-underline" data-testid={`gl-sup-toggle-${id}`}>
        <span className="flex flex-1 flex-wrap items-center gap-2 text-left">
          <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold ${TONE[m.status]}`}>{m.status}</span>
          <span className="font-bold text-slate-100">{m.label}</span>
          <span className="text-slate-500">{MK[mk]} · {g.away} @ {g.home}</span>
          <span className="text-slate-400">{odds(m.odds)} · edge {m.edge} · EV {m.exp_roi}%{m.support != null ? ` · support ${m.support}` : ""}</span>
        </span>
      </AccordionTrigger>
      <AccordionContent className="px-2 pb-3">
        {!d ? <p className="font-mono text-[10px] text-slate-500">Loading summary…</p>
          : d.error ? <p className="font-mono text-[10px] text-rose-400">Summary unavailable.</p> : <Detail d={d} />}
      </AccordionContent>
    </AccordionItem>
  );
};

export const LinesSupport = ({ games, apiDate }) => {
  const picks = games.flatMap((g) => Object.entries(g.markets || {})
    .filter(([, m]) => m && ["APPROVED", "VALUE", "WATCH"].includes(m.status))
    .map(([mk, m]) => ({ g, mk, m })))
    .sort((a, b) => (RANK[a.m.status] - RANK[b.m.status]) || (b.m.ev || 0) - (a.m.ev || 0));
  return (
    <div data-testid="gl-support">
      <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-slate-500">📋 Supporting Summary · APPROVED, VALUE & WATCH</p>
      {picks.length ? (
        <Accordion type="multiple" className="rounded-xl border border-slate-800 bg-slate-900/40">
          {picks.map(({ g, mk, m }) => <Item key={`${g.event_id}-${mk}`} g={g} mk={mk} m={m} apiDate={apiDate} />)}
        </Accordion>
      ) : <p className="font-mono text-[10px] text-slate-500" data-testid="gl-support-empty">No APPROVED, VALUE or WATCH picks on this slate.</p>}
    </div>
  );
};
